/* eslint-disable react/display-name */
/* eslint-disable react/jsx-wrap-multilines */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAuth } from '../../../hooks';
import './Date-Picker.css';

import { ArrowLeft, ArrowRight, Calendar, ChevronDown } from '../../../assets/icons';
import { IconSize } from '../../../constant/IconSize.constant';
import { getCurrentTheme } from '../../../stores/selectors/theme.selector';
import { isValidDate, parseDate } from '../../../utils/Date.Util';
import { Icon } from '../Icon.component';
import { FormInput, InputType } from '../Input.component';
import { ColorType, getHexColorByType } from '../shared/Colors.g';

export enum CustomInputType {
  DEFAULT,
  FORMINPUTDATE,
}
export interface InputProps {
  defaultDate?: Date;
  format?: string;
  onChange?: (date: any) => void;
  showMonthYearPicker?: boolean;
  inputBG?: string;
  maxDate?: Date | null;
  minDate?: Date | null;
  customInputType?: CustomInputType;
  showDefaultDate?: boolean;
  customFormInputDivWidth?: string;
  customFormInputErrorLabel?: string;
  disabled?: boolean;
  defaultSelectedDate?: any;
  customFontClass?: string;
  customFontColor?: string;
  inputId?: string;
  customWidthClass?: string;
  iconSize?: (typeof IconSize)[keyof typeof IconSize];
}

export const DateTimePicker = memo(
  ({
    defaultDate = new Date(),
    onChange,
    showMonthYearPicker = false,
    format = 'MMM yyyy',
    inputBG = 'bg-grey1 hover:bg-grey2',
    maxDate = null,
    minDate = null,
    customInputType = CustomInputType.DEFAULT,
    showDefaultDate = true,
    customFormInputDivWidth = '',
    customFormInputErrorLabel = '',
    disabled = false,
    defaultSelectedDate = null,
    customFontClass = 'font-inter',
    customFontColor = 'text-grey6',
    iconSize = IconSize.SIZE_20x20,
    inputId = '',
    customWidthClass = '',
  }: InputProps) => {
    const { t } = useTranslation();
    const auth = useAuth();
    const theme = useSelector(getCurrentTheme);

    const [selectedDate, setSelectedDate] = useState(showDefaultDate ? defaultDate : null);

    // Need to update the state when default changes
    // This will happen when clicks back button on browser
    // useEffect(() => {
    //   setSelectedDate(defaultDate);
    // }, [defaultDate]);

    // this hook take care for initial value for the datepicker regardless any other settings
    useEffect(() => {
      if (defaultSelectedDate) {
        setSelectedDate(parseDate(defaultSelectedDate, 'yyyy-MM-dd'));
      }
    }, [defaultSelectedDate]);

    const handleFormInputDate = useCallback(
      (date: any) => {
        if (isValidDate(date)) {
          setSelectedDate(parseDate(date));
          onChange && onChange(parseDate(date));
        }
      },
      [onChange],
    );

    const handleFormInputDateValidation = useCallback((val: any) => {
      return isValidDate(val);
    }, []);

    // The custom input is memoized so its component identity is stable across renders.
    // Recreating it every render would make react-datepicker unmount/remount the input
    // subtree (and the ChevronDown Icon, which fetches its SVG on every mount).
    const DateCustomInput = useMemo(
      () =>
        React.forwardRef<any, any>(({ value, onClick }: any, ref) => {
          if (customInputType === CustomInputType.FORMINPUTDATE) {
            return (
              <div ref={ref} className={`flex flex-row items-center ${customFormInputDivWidth}`}>
                <FormInput
                  defaultValue={value}
                  inputType={InputType.DATE}
                  placeholder='MM/DD/YYYY'
                  iconClassName={`pl-2 ${disabled ? 'text-grey4 opacity-50' : 'text-black'}`}
                  inputIconPadding='pl-8'
                  icon={Calendar}
                  iconSize={IconSize.SIZE_20x20}
                  onClickIcon={onClick}
                  onChange={(event) => handleFormInputDate(event.target.value)}
                  onValidation={handleFormInputDateValidation}
                  errorLabel={customFormInputErrorLabel}
                  width='100%'
                  disabled={disabled}
                  inputId={inputId}
                />
              </div>
            );
          }
          return (
            <button
              ref={ref}
              className={`react-datepicker__input-container-button ${inputBG} ${customWidthClass} 
                flex justify-between items-center rounded pr-2 pl-3 border border-grey1 text-left`}
              onClick={onClick}
              disabled={disabled}
              tabIndex={-1}
              aria-hidden='true'
            >
              <div className={`inline-block ${customFontColor}  ${customFontClass}`}>
                {value || t('select_month')}
              </div>
              <Icon
                src={ChevronDown}
                className='flex-shrink-0 ml-2'
                size={iconSize || IconSize.SIZE_20x20}
              />
            </button>
          );
        }),
      [
        customInputType,
        customFormInputDivWidth,
        disabled,
        inputId,
        customFormInputErrorLabel,
        inputBG,
        customWidthClass,
        customFontColor,
        customFontClass,
        iconSize,
        t,
        handleFormInputDate,
        handleFormInputDateValidation,
      ],
    );
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const ref = useRef<any>(null);

    // Accessibility Logic: Handles keyboard interaction for toggling the calendar dropdown (DatePicker)
    const DatePickerWrapperKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(!isOpen);
      }

      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const onDateChanged = useCallback(
      (date: any) => {
        setSelectedDate(date);
        onChange && onChange(date);
        setIsOpen(false);
        ref.current.focus();
      },
      [onChange],
    );

    const renderArrow = (arrow: any, alt: string) => {
      return <Icon src={arrow} size={IconSize.SIZE_20x20} alt={alt} />;
    };

    const onCalendarOpen = () => {
      setIsOpen(true);
    };

    // Accessibility Logic: When the date picker is open,
    // this effect applies custom styling to the selected or keyboard-selected month element in the calendar.
    useEffect(() => {
      if (isOpen) {
        const monthSelectedElement = document.querySelector(
          '.react-datepicker__month--selected',
        ) as HTMLElement;
        if (monthSelectedElement && monthSelectedElement.getAttribute('aria-selected')) {
          monthSelectedElement.style.backgroundColor = theme.colorPalettes.primary;
          monthSelectedElement.style.color = ColorType.WHITE;
        } else {
          const keyboardSelected = document.querySelector(
            '.react-datepicker__month-text--keyboard-selected',
          ) as HTMLElement;
          if (keyboardSelected) {
            keyboardSelected.style.backgroundColor = getHexColorByType(ColorType.WHITE);
          }
        }
      }
    }, [isOpen, theme.colorPalettes.primary]);

    return (
      <div
        className='inline-block rounded focus:outline focus:outline-2 focus:outline-[#0367CC]'
        ref={ref}
        role='button'
        tabIndex={0}
        onKeyDown={DatePickerWrapperKeyDown}
      >
        <DatePicker
          open={isOpen}
          onYearChange={onCalendarOpen}
          onCalendarOpen={onCalendarOpen}
          locale={auth.user.attributes.profile?.sitehostDashboardLanguage}
          selected={selectedDate}
          onChange={(date) => onDateChanged(date)}
          dateFormat={format}
          showMonthYearPicker={showMonthYearPicker}
          customInput={<DateCustomInput />}
          maxDate={maxDate}
          minDate={minDate}
          previousYearButtonLabel={renderArrow(ArrowLeft, 'icon_alt_previous_year')}
          nextYearButtonLabel={renderArrow(ArrowRight, 'icon_alt_next_year')}
          previousMonthButtonLabel={renderArrow(ArrowLeft, 'icon_alt_previous_month')}
          nextMonthButtonLabel={renderArrow(ArrowRight, 'icon_alt_next_month')}
          disabled={disabled}
          onClickOutside={() => {
            setIsOpen(false);
          }}
        />
      </div>
    );
  },
);

DateTimePicker.displayName = 'DateTimePicker';
